// SpawnedConvList — US-136 + US-137.
//
// Shows the most recent 8 conversations spawned by this campaign. The
// composite is intentionally lean — Inbox is the canonical place to manage
// these threads. Footer includes the "View all →" deep-link.

import { Link } from '@tanstack/react-router';

import { useCampaignConversations, type CampaignConversation } from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { Avatar, Card, Pill } from '@barel/ui';

export interface SpawnedConvListProps {
  campaignId: string;
}

export function SpawnedConvList({ campaignId }: SpawnedConvListProps) {
  const t = useT('campaigns');
  const query = useCampaignConversations(campaignId);
  const convs = query.data?.conversations ?? [];

  return (
    <Card padding="lg" radius="2xl" data-testid="spawned-conv-list">
      <div className="text-tertiary text-xs font-bold uppercase mb-3">{t('detail.convsTitle')}</div>
      {convs.length === 0 ? (
        <p className="text-tertiary text-xs italic">{t('detail.convsEmpty')}</p>
      ) : (
        <>
          <ul className="flex flex-col">
            {convs.map((conv: CampaignConversation) => (
              <li
                key={conv.id}
                className="flex items-center gap-3 py-2 border-b border-default last:border-b-0"
              >
                <Avatar
                  size="sm"
                  tone="identity"
                  identityColor={conv.contactColor ?? '#5b6ef8'}
                  initials={conv.contactInitials}
                  alt={conv.contactName}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-primary text-sm font-semibold truncate">
                    {conv.contactName}
                  </div>
                </div>
                {conv.isVeryHot ? (
                  <Pill tone="error" size="xs">
                    {t('detail.veryHotBadge')}
                  </Pill>
                ) : null}
              </li>
            ))}
          </ul>
          <p className="text-tertiary text-xs italic mt-3">{t('detail.convsFooter')}</p>
          <div className="mt-2 flex justify-end">
            <Link
              to="/inbox"
              search={{ accountId: undefined, mode: undefined, selectedId: undefined }}
              className="text-accent text-xs font-semibold"
              data-testid="spawned-conv-view-all"
            >
              {t('detail.convsViewAll')}
            </Link>
          </div>
        </>
      )}
    </Card>
  );
}
